import { useState, useContext, useRef } from "react";
import { db } from "../../firebase/config";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import OrderQuestionnaireSectionFields from "./OrderQuestionnaireSectionFields";
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import DashboardColorPicker from "../dashboard/DashboardColorPicker"
import uuid from 'react-uuid'
import Tooltip from "../common/Tooltip";
import Modal from "../common/Modal";
import { timestamp } from "../../firebase/config";
import { client } from "../../hooks/Client";
import QuestionnaireField from "./QuestionnaireField";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import MSIQuestionnaireField from "./MSIQuestionnaireField";
import EffectMeta from "../effects/EffectMeta";
import getDropZone from "../../helpers/questionnaire/getDropZone";
import reorderFields from "../../helpers/questionnaire/reorderFields";
import sortSectionFields from "../../helpers/questionnaire/sortSectionFields";
import notDeleted from "../../helpers/notDeleted";
import QuestionTypeTag from "./QuestionTypeTag";
import ShowWhenChip from "./ShowWhenChip";
import canReorderWithRules from "../../helpers/questionnaire/canReorderWithRules";
import isQualifyingQuestion from '../../helpers/questionnaire/isQualifyingQuestion'
import removeFieldFromQuestionnaire, { removeFieldCopy } from "../../helpers/questionnaire/removeFieldFromQuestionnaire";
import scaleStartLabel from "../../helpers/questionnaire/scaleStartLabel";

// AI-CONTEXT: See docs/features/questionnaire-sections.md
// Native HTML5 drag-and-drop (replaces react-beautiful-dnd, which can't nest).
// A section is the only valid "inside" drop target; questions are leaves.
const OrderQuestionnaireFieldsView = ({ questionnaireFields }) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // States
    const [editQuestion, setEditQuestion] = useState(null)
    const [showMSI, setShowMSI] = useState(null);
    const [dropIndicator, setDropIndicator] = useState(null) // { docid, edge }
    const [sectionToDelete, setSectionToDelete] = useState(null)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [optionsSectionId, setOptionsSectionId] = useState(null)
    const [reorderBlocked, setReorderBlocked] = useState('')
    const [fieldToRemove, setFieldToRemove] = useState(null)

    // Refs
    const draggedRef = useRef(null)

    // Hide soft-deleted fields from every derivation (ADR-008).
    const activeFields = (questionnaireFields || []).filter(notDeleted)

    // The section whose options modal is open (derived live so the swatch
    // selection reflects the latest saved colour).
    const optionsSection = optionsSectionId
        ? activeFields.find(f => f.ID === optionsSectionId)
        : null

    // Only top-level items (section markers + top-level questions), in Position order.
    const topLevelFields = activeFields.filter(field => field.SectionID === '')

    // --- Drag-and-drop -----------------------------------------------------

    const clearDrag = () => {
        draggedRef.current = null
        setDropIndicator(null)
    }

    // Sections accept an "inside" drop (a question joins them). A section can't go
    // inside another section, so coerce that to before/after at the midpoint.
    const computeEdge = (e, field) => {
        const rect = e.currentTarget.getBoundingClientRect()
        let edge = getDropZone({ pointer: e.clientY, rect, axis: 'y' })
        const dragged = draggedRef.current
        const sectionAcceptsInside = field.Type === 'section' && dragged?.Type !== 'section'
        if (edge === 'inside' && !sectionAcceptsInside) {
            edge = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
        }
        return edge
    }

    const onRowDragStart = (field) => (e) => {
        e.stopPropagation()
        draggedRef.current = field
        e.dataTransfer.effectAllowed = 'move'
        try { e.dataTransfer.setData('text/plain', field.ID) } catch (err) { /* some browsers */ }
    }

    const onRowDragOver = (field) => (e) => {
        e.preventDefault()
        e.stopPropagation()
        const dragged = draggedRef.current
        if (!dragged || dragged.docid === field.docid) return
        const edge = computeEdge(e, field)
        setDropIndicator(prev => (prev?.docid === field.docid && prev?.edge === edge) ? prev : { docid: field.docid, edge })
    }

    const onRowDrop = (field) => (e) => {
        e.preventDefault()
        e.stopPropagation()
        const dragged = draggedRef.current
        const edge = dropIndicator?.docid === field.docid ? dropIndicator.edge : computeEdge(e, field)
        clearDrag()
        if (!dragged || dragged.docid === field.docid) return
        performDrop(dragged, field, edge)
    }

    const onRowDragEnd = () => clearDrag()

    // Compute and persist the new ordering after a drop.
    const performDrop = (dragged, target, edge) => {
        // Een triggervraag moet vóór de vraag blijven staan die ervan afhangt.
        // Blokkeren met een melding — nooit stil de regel wissen.
        // AI-CONTEXT: See docs/features/conditional-questions.md
        const verdict = canReorderWithRules({ fields: activeFields, dragged, target, edge })
        if (!verdict.allowed) {
            setReorderBlocked(verdict.reason)
            return
        }
        setReorderBlocked('')

        const intoSection = edge === 'inside' && target.Type === 'section'
        const destSectionId = intoSection ? target.ID : (target.SectionID || '')
        const positionField = destSectionId === '' ? 'Position' : 'SectionPosition'

        // Sections live only at top-level — never nest one inside another (or itself).
        if (dragged.Type === 'section' && destSectionId !== '') return

        const siblings = destSectionId === ''
            ? activeFields.filter(f => f.SectionID === '')
            : sortSectionFields(activeFields.filter(f => f.SectionID === destSectionId))

        // Dropping inside a section header appends to the end (no in-list target).
        const targetField = intoSection ? null : target

        const updates = reorderFields({ siblings, draggedField: dragged, targetField, edge, positionField })

        updates.forEach(u => {
            db.collection('QuestionnaireFields').doc(u.docid).update({ [positionField]: u[positionField] })
        })

        // Moved to a different group → update its SectionID.
        if ((dragged.SectionID || '') !== destSectionId) {
            db.collection('QuestionnaireFields').doc(dragged.docid).update({ SectionID: destSectionId })
        }

        setSaved(true)
    }

    // CSS class for the drop indicator on a given row/section.
    const dropClass = (docid) => {
        if (dropIndicator?.docid !== docid) return ''
        if (dropIndicator.edge === 'before') return 'qb-drop-before'
        if (dropIndicator.edge === 'after') return 'qb-drop-after'
        if (dropIndicator.edge === 'inside') return 'qb-drop-inside'
        return ''
    }

    // Drag API handed to the in-section list so its rows share the same model.
    const dragApi = { onRowDragStart, onRowDragOver, onRowDrop, onRowDragEnd, performDrop, clearDrag, draggedRef, dropClass, setDropIndicator, dropIndicator }

    // --- Field handlers ----------------------------------------------------

    const titleHandler = (docid, value) => {
        db.collection('QuestionnaireFields').doc(docid).update({ Question: value })
            .then(() => setSaved(true))
            .catch(err => console.log(err))
    }

    const editHandler = (id) => setEditQuestion(prev => (prev === id ? null : id))

    // Persist a new accent colour for the open section.
    const sectionColorHandler = (hex) => {
        if (!optionsSection) return
        db.collection('QuestionnaireFields').doc(optionsSection.docid).update({ Color: hex })
            .then(() => setSaved(true))
            .catch(err => console.log(err))
    }

    const showMSIHandler = (id) => setShowMSI(prev => (prev === id ? null : id))

    // ADR-008 soft delete: flag the doc, keep its links so it stays recoverable.
    const softDelete = (docid) =>
        db.collection('QuestionnaireFields').doc(docid).update({
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        })

    // Een meetstandaardvraag wordt losgekoppeld, een eigen vraag soft-deleted.
    // AI-CONTEXT: See docs/features/msi-effect-questionnaire-attach.md
    const removeField = (field) =>
        db.collection('QuestionnaireFields').doc(field.docid).update(
            removeFieldFromQuestionnaire(field, { timestamp, userId: auth?.ID })
        )

    // Eerst bevestigen: de lijst schuift na een verwijdering op, zodat de
    // volgende prullenbak precies onder de muis komt (testronde 2026-09-10, B1).
    const deleteHandler = (field) => setFieldToRemove(field)

    const confirmRemove = () => {
        if (!fieldToRemove) return
        removeField(fieldToRemove)
        setSaved(true)
        setFieldToRemove(null)
    }
    const removeCopy = fieldToRemove ? removeFieldCopy(fieldToRemove) : null

    // Section delete is a choice — open a modal (default: keep the questions).
    const openSectionDeleteModal = (sectionField) => {
        setSectionToDelete(sectionField)
        setDeleteModalOpen(true)
    }

    const closeSectionDeleteModal = () => {
        setDeleteModalOpen(false)
        setSectionToDelete(null)
    }

    const sectionChildren = (sectionField) =>
        activeFields.filter(f => f.SectionID === sectionField.ID)

    // Orphan the questions back to top-level, then soft-delete the section marker.
    const deleteSectionKeepQuestions = () => {
        if (!sectionToDelete) return
        sectionChildren(sectionToDelete).forEach(child => {
            db.collection('QuestionnaireFields').doc(child.docid).update({ SectionID: '' })
        })
        softDelete(sectionToDelete.docid)
        setSaved(true)
        closeSectionDeleteModal()
    }

    // Soft-delete the section marker and every question inside it.
    const deleteSectionAndQuestions = () => {
        if (!sectionToDelete) return
        sectionChildren(sectionToDelete).forEach(child => removeField(child))
        softDelete(sectionToDelete.docid)
        setSaved(true)
        closeSectionDeleteModal()
    }

    const duplicateHandler = (field) => {
        const fieldID = uuid()

        db.collection('QuestionnaireFields').doc().set({
            Compagny: client,
            CompagnyID: client,
            ID: fieldID,
            Timestamp: timestamp,
            QuestionnaireID: field.QuestionnaireID,
            Type: field.Type,
            Question: ('Kopie van ' + field.Question),
            Explainer: field.Explainer,
            ReachStart: Number(field.ReachStart),
            ReachStartLable: scaleStartLabel(field),
            ReachEnd: Number(field.ReachEnd),
            ReachEndLabel: field.ReachEndLabel,
            Position: questionnaireFields.length + 1,
            Multiple: field.Multiple,
            SectionID: field.SectionID,
            LiveReportHidden: false,
            MSIId: field.MSIId,
            Standard: field.Standard,
            MatrixBipolar: field.MatrixBipolar === true,
        }).then(() => {
            duplicateOptions(field.ID, fieldID, field.Question)
            duplicateMatrixRows(field.ID, fieldID, field.Question)
            duplicateMatrixColumns(field.ID, fieldID, field.Question)
        })
    }

    const duplicateOptions = (id, fieldID, question) => {
        db.collection('MultipleQuestionOptions')
            .where('CompagnyID', '==', client)
            .where('Field', '==', id)
            .get()
            .then((snapshot) => {
                snapshot.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
                    db.collection('MultipleQuestionOptions').doc().set({
                        Option: doc.data().Option,
                        Question: question,
                        Field: fieldID,
                        Compagny: client,
                        CompagnyID: client,
                        Timestamp: timestamp,
                        Position: doc.data().Position,
                        ID: uuid(),
                    })
                })
            })
    }

    const duplicateMatrixRows = (id, fieldID, question) => {
        db.collection('MatrixQuestionRows')
            .where('Compagny', '==', client)
            .where('Field', '==', id)
            .get()
            .then((snapshot) => {
                snapshot.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
                    db.collection('MatrixQuestionRows').doc().set({
                        Title: doc.data().Title,
                        TitleRight: doc.data().TitleRight || '',
                        Question: question,
                        Field: fieldID,
                        Compagny: client,
                        CompagnyID: client,
                        Timestamp: timestamp,
                        Position: doc.data().Position,
                        ID: uuid(),
                    })
                })
            })
    }

    const duplicateMatrixColumns = (id, fieldID, question) => {
        db.collection('MatrixQuestionColumns')
            .where('Compagny', '==', client)
            .where('Field', '==', id)
            .get()
            .then((snapshot) => {
                snapshot.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
                    db.collection('MatrixQuestionColumns').doc().set({
                        Title: doc.data().Title,
                        Question: question,
                        Field: fieldID,
                        Compagny: client,
                        CompagnyID: client,
                        Timestamp: timestamp,
                        Position: doc.data().Position,
                        ID: uuid(),
                    })
                })
            })
    }

    // --- Render ------------------------------------------------------------

    const childCountOf = (sectionId) => activeFields.filter(f => f.SectionID === sectionId).length

    const renderSection = (field, sectionNumber, firstQuestionNumber) => {
        const childCount = childCountOf(field.ID)
        // De sectie 'Kwalificering' wordt beheerd door de schakelaar op het
        // onderzoek: geen titel, opties, verwijderen of slepen hier.
        // AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md
        const locked = isQualifyingQuestion(field)
        return (
            <div
                key={field.ID}
                className={`order-questionnaire-field-item-container qb-section ${dropClass(field.docid)}`}
                style={{ '--sec': field.Color || '#ffcb0f' }}
                onDragOver={onRowDragOver(field)}
                onDrop={onRowDrop(field)}
            >
                <div className='qb-section-head'>
                    {!locked && (
                        <Tooltip content="Versleep sectie" width={'24px'}>
                            <span className="qb-grip" draggable='true' onDragStart={onRowDragStart(field)} onDragEnd={onRowDragEnd}>
                                <DragIndicatorOutlinedIcon />
                            </span>
                        </Tooltip>
                    )}
                    <span className="qb-sec-badge">Sectie {sectionNumber}</span>
                    {locked ? (
                        <span className="qb-sec-title qb-locked-title">{field.Question}</span>
                    ) : (
                        <input
                            className="qb-sec-title"
                            type="text"
                            defaultValue={field.Question}
                            onChange={(e) => titleHandler(field.docid, e.target.value)}
                            aria-label="Sectie titel"
                        />
                    )}
                    <span className="qb-sec-count">{childCount} {childCount === 1 ? 'vraag' : 'vragen'}</span>
                    {!locked && (<>
                    <Tooltip content="Sectie-opties" width={'32px'}>
                        <button className="qb-iconbtn" onClick={() => setOptionsSectionId(field.ID)} aria-label="Sectie-opties">
                            <TuneOutlinedIcon />
                        </button>
                    </Tooltip>
                    <Tooltip content="Verwijder sectie" width={'32px'}>
                        <button className="qb-iconbtn danger" onClick={() => openSectionDeleteModal(field)} aria-label="Verwijder sectie">
                            <DeleteOutlinedIcon />
                        </button>
                    </Tooltip>
                    </>)}
                </div>
                {locked && (
                    <p className="qb-locked-note">Vaste vragen. Aan en uit zet je bij het onderzoek, onder 'Wie doet mee'.</p>
                )}
                <div className="qb-section-body">
                    <OrderQuestionnaireSectionFields section={field.ID} sectionField={field} dragApi={dragApi} questionnaireFields={activeFields} firstNumber={firstQuestionNumber} />
                </div>
            </div>
        )
    }

    const renderQuestion = (field, questionNumber, questionIndex) => {
        const isStandard = field.Standard === 'true'
        return (
            <div
                key={field.ID}
                className={`order-questionnaire-field-item-container ${dropClass(field.docid)}`}
                onDragOver={onRowDragOver(field)}
                onDrop={onRowDrop(field)}
            >
                <div className={`qb-q ${editQuestion === field.ID ? 'open' : ''} ${field.ShowWhen?.FieldID ? 'qb-conditional' : ''}`}>
                    <div className="qb-q-row">
                        <Tooltip content="Versleep vraag" width={'24px'}>
                            <span className="qb-grip" draggable='true' onDragStart={onRowDragStart(field)} onDragEnd={onRowDragEnd}>
                                <DragIndicatorOutlinedIcon />
                            </span>
                        </Tooltip>
                        <span className="qb-num">{questionNumber}</span>
                        {isStandard ? (
                            <div className="qb-q-std">
                                <EffectMeta item={field.EffectId[0]} />
                                <a href="https://meetstandaard.nl" target="_blank" rel="noopener noreferrer">(meetstandaard.nl)</a>
                            </div>
                        ) : (
                            <>
                                <input className="qb-q-title" type="text" defaultValue={field.Question} placeholder="Typ hier je vraag…" onChange={(e) => titleHandler(field.docid, e.target.value)} />
                                <ShowWhenChip field={field} allFields={activeFields} />
                                <QuestionTypeTag type={field.Type} />
                            </>
                        )}
                        <div className="qb-actions">
                            {isStandard ? (
                                <>
                                    <Tooltip content="Bekijk gestandaardiseerde vraag" width={'32px'}>
                                        <button className="qb-iconbtn" onClick={() => showMSIHandler(field.ID)} aria-label="Bekijk gestandaardiseerde vraag">
                                            <SearchOutlinedIcon />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Verwijderen" width={'32px'}>
                                        <button className="qb-iconbtn danger" onClick={() => deleteHandler(field)} aria-label="Verwijderen">
                                            <DeleteOutlinedIcon />
                                        </button>
                                    </Tooltip>
                                </>
                            ) : (
                                <>
                                    <Tooltip content="Bewerken" width={'32px'}>
                                        <button className="qb-iconbtn primary" onClick={() => editHandler(field.ID)} aria-label="Bewerken">
                                            <EditOutlinedIcon />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Dupliceren" width={'32px'}>
                                        <button className="qb-iconbtn" onClick={() => duplicateHandler(field)} aria-label="Dupliceren">
                                            <ContentCopyOutlinedIcon />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Verwijderen" width={'32px'}>
                                        <button className="qb-iconbtn danger" onClick={() => deleteHandler(field)} aria-label="Verwijderen">
                                            <DeleteOutlinedIcon />
                                        </button>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    </div>
                    {editQuestion === field.ID && (
                        <QuestionnaireField field={field} index={questionIndex} position={activeFields.length} allFields={activeFields} />
                    )}
                    {showMSI === field.ID && (
                        <MSIQuestionnaireField field={field} />
                    )}
                </div>
            </div>
        )
    }

    // Secties hebben hun eigen nummer; vragen lopen door over de hele
    // vragenlijst, ook die in een sectie. Losse tellers gaven onder een sectie
    // met vraag 1 t/m 3 weer een vraag 1 (testronde 2026-10-28, B3).
    // `questionIndex` blijft de plek tussen de losse vragen, zoals
    // QuestionnaireField die al kende.
    let sectionNumber = 0
    let questionNumber = 0
    let questionIndex = 0

    return (
        <div id='order-questionniare-field-container'>
            {reorderBlocked && (
                <p className="qb-reorder-blocked" role="alert">{reorderBlocked}</p>
            )}
            {topLevelFields.map((field) => {
                if (field.Type === 'section') {
                    sectionNumber += 1
                    const firstQuestionNumber = questionNumber + 1
                    questionNumber += childCountOf(field.ID)
                    return renderSection(field, sectionNumber, firstQuestionNumber)
                }
                questionNumber += 1
                questionIndex += 1
                return renderQuestion(field, questionNumber, questionIndex - 1)
            })}
            <Modal
                modalOpen={fieldToRemove !== null}
                setModalOpen={(open) => { if (!open) setFieldToRemove(null) }}
                pageHeader={removeCopy?.title || ''}
                confirmLabel={removeCopy?.confirmLabel || ''}
                confirm={confirmRemove}
                content={<p>{removeCopy?.body || ''}</p>}
            />
            <Modal
                modalOpen={deleteModalOpen}
                setModalOpen={setDeleteModalOpen}
                pageHeader={'Sectie verwijderen'}
                hideConfirm={true}
                content={
                    <div className="section-delete-options">
                        <p>Wil je de vragen in deze sectie behouden of ook verwijderen?</p>
                        <button className="button-simple" onClick={deleteSectionKeepQuestions}>
                            Sectie verwijderen, vragen behouden
                        </button>
                        <button className="button-simple" onClick={deleteSectionAndQuestions}>
                            Sectie én vragen verwijderen
                        </button>
                    </div>
                }
            />
            <Modal
                modalOpen={!!optionsSection}
                setModalOpen={(open) => { if (!open) setOptionsSectionId(null) }}
                pageHeader={'Sectie-opties'}
                hideConfirm={true}
                content={
                    optionsSection && (
                        <div className="section-options-modal">
                            <p className="section-options-label">Kleur van de sectie</p>
                            <DashboardColorPicker value={optionsSection.Color} onChange={sectionColorHandler} />
                        </div>
                    )
                }
            />
        </div>
    );
}

export default OrderQuestionnaireFieldsView
